/**
 * Compose a 2–3 line, copy-paste-ready outreach summary from a comparison
 * result + the LLM advisor markdown.
 *
 * Strategy:
 *   1. Find the "**Recommendation**" section in the advisor markdown.
 *   2. Match any `region_short` or `region_name` mentioned in that section
 *      back to one of `result.columns`.
 *   3. That column's `best_fit_rails[0]` + `top_guarantees[0]` become the
 *      concrete "how it lands + who backs it" line.
 *
 * Falls back gracefully when the advisor markdown is empty / doesn't name a
 * region — still returns a valid summary based on the first column.
 */
export function buildActionableSummary(result, advisorMarkdown = "") {
  if (!result || !result.columns?.length) return "";

  const columns = result.columns;
  const rec = findRecommendedColumn(columns, advisorMarkdown) || columns[0];

  const rail = rec.best_fit_rails?.[0];
  const guarantee = rec.top_guarantees?.[0];

  const amountFmt = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(Number(result.loan_amount) || 0);

  const railClause = rail ? ` — disburse via ${rail}` : "";
  const guaranteeClause = guarantee ? `, backed by ${guarantee}` : "";

  return (
    `Recommended: ${rec.region_name} for ${result.business_name}'s ` +
    `${result.industry_name} facility (${result.currency_code} ${amountFmt} · ${result.tenure_months} mo${railClause}${guaranteeClause}).\n` +
    `Rate range: ${rec.rate_range}. Reference: ${rec.rate_hint}.`
  );
}

function findRecommendedColumn(columns, advisorMarkdown) {
  if (!advisorMarkdown) return null;

  // Isolate the "**Recommendation**" section — everything after the heading
  // until the next markdown heading or end-of-string.
  const recMatch = advisorMarkdown.match(
    /\*\*Recommendation\*\*([\s\S]*?)(?=\n\s*\*\*|\n\s*#{1,6}\s|$)/i
  );
  const section = recMatch ? recMatch[1] : advisorMarkdown;

  // First column whose short-name OR full name appears in the section wins.
  // Prefer short-name because it's more distinctive (e.g. "ECOWAS" vs "West Africa").
  for (const c of columns) {
    const short = c.region_short || "";
    const name = c.region_name || "";
    const shortRe = short ? new RegExp(`\\b${escapeRegex(short)}\\b`, "i") : null;
    const nameRe = name ? new RegExp(`\\b${escapeRegex(name)}\\b`, "i") : null;
    if ((shortRe && shortRe.test(section)) || (nameRe && nameRe.test(section))) {
      return c;
    }
  }
  return null;
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
